/**
 * WEB-1988 — на каком ХОСТЕ раздаётся публичный вход сборки (`public.html`).
 *
 * Правда одна и живёт здесь; её читают оба конца:
 *  - раздатчик `frontend/Caddyfile` (хост-правило, которое отдаёт public.html
 *    вместо index.html);
 *  - браузер — та же SPA выбирает публичный роутер по `isCustomDomain()`
 *    (`utils/publicHost.ts`).
 * Совпадение концов держит сторож `publicEntryHosts.test.ts`.
 */

/**
 * Поддомен рендера: сюда воркер Cloudflare
 * (`infra/cloudflare/w2a-funnel-router.js`) проксирует запросы ВСЕХ купленных
 * клиентских доменов, подменяя заголовок Host. В браузере адрес остаётся
 * клиентским (по нему SPA и резолвит воронку), а сервер видит именно этот
 * хост — поэтому для раздатчика он тоже «публичный», хотя формально это
 * поддомен нашего апекса.
 */
export const PUBLIC_RENDER_SUBDOMAIN = 'render';

/**
 * WEB-2115 — поддомен ссылки предпросмотра (`preview.<апекс>/<токен>`). Его
 * открывает ПОСТОРОННИЙ человек, которому автор прислал ссылку, поэтому
 * раздатчик отдаёт здесь лёгкую публичную оболочку, а публичный роутер — не
 * резолв домена, а страницу предпросмотра (`router/publicRouter.tsx`).
 *
 * Имя вшито в код (WEB-2793): одно на сервер (`PREVIEW_LINKS_DEFAULT_SUBDOMAIN`),
 * кабинет и раздатчик (`frontend/Caddyfile`), переменных окружения для него
 * нет. Файл читает и `vite.config.ts` (через `publicEntryDevMiddleware.ts`),
 * поэтому он без импортов; роутер берёт имя через `PUBLIC_PREVIEW_SUBDOMAIN`
 * в `config/public-host.ts`.
 */
export const DEFAULT_PREVIEW_SUBDOMAIN = 'preview';

/** True — это хост страницы получателя ссылки предпросмотра. */
export function isPreviewLinkHost(
  host: string,
  apex: string,
  previewSubdomain: string = DEFAULT_PREVIEW_SUBDOMAIN,
): boolean {
  return (
    normalizeHost(host) ===
    `${previewSubdomain}.${normalizeHost(apex)}`
  );
}

/**
 * WEB-2187 — пути, которым публичная оболочка полагается на ЛЮБОМ хосте,
 * включая наш апекс и его поддомены.
 *
 * `/handoff/*` — страницы возврата покупателя после оплаты (успех, отмена,
 * волшебная ссылка из письма). Их адрес собирает бэкенд из `HANDOFF_DOMAIN`
 * (`PaywallsService.buildDefaultSuccessUrl`), и этим доменом бывает и наш
 * `go.<апекс>`, и клиентский домен — то есть по хосту эти страницы не
 * опознаются, только по пути.
 *
 * ЗАЧЕМ ИМЕННО ПУБЛИЧНАЯ. Здесь стоит НАСТОЯЩИЙ ПОКУПАТЕЛЬ сразу после
 * оплаты, а на экране его почта, имя и код доступа. Кабинетная оболочка несёт
 * кабинетную наблюдаемость с ВКЛЮЧЁННОЙ записью экрана (session-replay,
 * WEB-469) — ровно то, что WEB-2178 сняла с публичного входа воронки.
 * Прежнее правило было обратным («эти пути — всё-таки кабинет»), потому что
 * страницы были написаны на Ant Design; теперь они без него
 * (`pages/PublicHandoff*`) и живут в публичном роутере.
 */
export const PUBLIC_ENTRY_PATH_PREFIXES = ['/handoff'] as const;

/** Нормализация хоста: нижний регистр + без завершающей точки (FQDN-форма). */
function normalizeHost(host: string): string {
  return host.toLowerCase().replace(/\.$/, '');
}

/**
 * True — хосту раздаётся `public.html` (страница покупателя воронки).
 *
 * Правило зеркалит `isCustomDomain()`: всё, что НЕ наш апекс, не его поддомен
 * и не локальная разработка, — чужой домен, то есть воронка. Два добавления —
 * поддомен рендера (см. {@link PUBLIC_RENDER_SUBDOMAIN}) и поддомен ссылки
 * предпросмотра (WEB-2115, {@link isPreviewLinkHost}).
 */
export function isPublicEntryHost(
  host: string,
  apex: string,
  previewSubdomain: string = DEFAULT_PREVIEW_SUBDOMAIN,
): boolean {
  const normalized = normalizeHost(host);
  const normalizedApex = normalizeHost(apex);

  if (normalized === `${PUBLIC_RENDER_SUBDOMAIN}.${normalizedApex}`) return true;
  if (isPreviewLinkHost(normalized, normalizedApex, previewSubdomain)) return true;
  if (normalized === 'localhost' || normalized === '127.0.0.1') return false;
  if (normalized === normalizedApex) return false;
  if (normalized.endsWith(`.${normalizedApex}`)) return false;
  return true;
}

/**
 * WEB-1820 — множество хостов СТЕНДА платформы: наш собственный apex
 * (`PUBLIC_APEX`), любой его поддомен и локальная разработка.
 *
 * ЗАЧЕМ. QA не может проверить баннер согласия живьём: он показывается
 * только посетителям из ЕС/Великобритании (`requiresEuConsent`), а
 * браузерные дорожки QA выходят в сеть не из ЕС — гео-условие само по себе
 * баннер живьём не проверить никаким браузером на любом хосте. Служебный
 * параметр адреса (`forceConsent`, см. `utils/analytics/forceConsent.ts`)
 * обходит это ТОЛЬКО на хостах из этого множества.
 *
 * ГРАНИЦА. Купленный клиентский домен (реальный трафик покупателя) сюда не
 * попадает никогда: он не равен apex и не оканчивается на apex по
 * построению — то же сравнение, что `isPublicEntryHost` уже использует для
 * противоположного решения (эти два предиката НЕ дополняют друг друга до
 * полного множества хостов: поддомен рендера `isPublicEntryHost` считает
 * публичным входом, а СТЕНДОМ для forceConsent он не является — сервер
 * этого поддомена не покупатель, а прокси Cloudflare для чужих доменов).
 */
export function isStagingConsentHost(host: string, apex: string): boolean {
  const normalized = normalizeHost(host);
  const normalizedApex = normalizeHost(apex);

  if (normalized === 'localhost' || normalized === '127.0.0.1') return true;
  if (normalized === normalizedApex) return true;
  if (normalized.endsWith(`.${normalizedApex}`)) return true;
  return false;
}
